import React, { useEffect, useState } from 'react';
import { Table, Button, Modal, Form, Input, message, Popconfirm, Select, Row, Col, Upload, Space, Tag, Card, Divider, List, Typography } from 'antd';
import { PlusOutlined, EditOutlined, DeleteOutlined, TeamOutlined, UploadOutlined, MinusCircleOutlined, FileOutlined } from '@ant-design/icons';
import {
  getSubcontractors,
  createSubcontractor,
  updateSubcontractor,
  deleteSubcontractor,
  getSubcontractorEmployees,
  getSubcontractor,
  bulkUploadSubcontractorDocuments,
  deleteSubcontractorDocument,
  imagepath
} from '../api';
import { useNavigate } from 'react-router-dom';

const { Option } = Select;

const subDocTypes = [
  { value: 'trade_license', label: 'Trade License' },
  { value: 'work_contract', label: 'Work Contract' },
  { value: 'workmen_insurance', label: 'Workmen Insurance' },
  { value: 'staff_documents', label: 'Staff Documents' },
  { value: 'other', label: 'Other' },
];

const { Text } = Typography;

const isImage = (fileName) => /\.(jpg|jpeg|png|gif)$/i.test(fileName);
const isPdf = (fileName) => /\.(pdf)$/i.test(fileName);

const Subcontractors = () => {
  const [subcontractors, setSubcontractors] = useState([]);
  const [loading, setLoading] = useState(false);
  const [modalVisible, setModalVisible] = useState(false);
  const [docViewModalVisible, setDocViewModalVisible] = useState(false);
  const [viewingSub, setViewingSub] = useState(null);
  const [editing, setEditing] = useState(null);
  const [form] = Form.useForm();
  const [selectedSubId, setSelectedSubId] = useState(null);
  const navigate = useNavigate();

  const fetchSubcontractors = async () => {
    setLoading(true);
    const res = await getSubcontractors();
    setLoading(false);
    if (res.success) {
      setSubcontractors(res.data);
    } else {
      message.error(res.message || 'Failed to fetch subcontractors');
    }
  };

  useEffect(() => {
    fetchSubcontractors();
  }, []);

  const [initialValues, setInitialValues] = useState(null);

  const openModal = async (record = null) => {
    setEditing(record);
    if (record) {
      setLoading(true);
      const res = await getSubcontractor(record.id);
      setLoading(false);
      if (res.success) {
        const subData = { ...res.data };
        if (subData.documents && subData.documents.length > 0) {
          subData.documents = subData.documents.map(doc => ({
            ...doc,
            document_file: doc.document_file ? [{
              uid: doc.id,
              name: doc.original_filename || doc.document_file.split('/').pop(),
              status: 'done',
              url: `${imagepath}${doc.document_file}`,
            }] : []
          }));
        }
        setInitialValues(subData);
        form.setFieldsValue(subData);
      }
    } else {
      setInitialValues(null);
      form.resetFields();
    }
    setModalVisible(true);
  };

  const handleOk = async () => {
    try {
      const values = await form.validateFields();
      setLoading(true);

      // Handle document updates if editing
      if (editing) {
        // 1. Delete removed documents
        const removedDocIds = (initialValues.documents || []).filter(initDoc => {
          const currentDoc = values.documents?.find(d => d.id === initDoc.id);
          if (!currentDoc) return true; // Row was removed
          const hasNewFile = currentDoc.document_file?.[0]?.originFileObj;
          const hasNoFile = !currentDoc.document_file || currentDoc.document_file.length === 0;
          return !!(hasNewFile || hasNoFile);
        }).map(doc => doc.id);

        if (removedDocIds.length > 0) {
          await Promise.all(removedDocIds.map(docId => deleteSubcontractorDocument(editing.id, docId)));
        }

        // 2. Update subcontractor info
        const res = await updateSubcontractor(editing.id, values);
        if (!res.success) throw new Error(res.message);

        // 3. Upload new documents
        if (values.documents && values.documents.length > 0) {
          const docsToUpload = values.documents
            .filter(doc => doc.document_file?.[0]?.originFileObj)
            .map(doc => ({
              document_type: doc.document_type,
              expiry_date: doc.expiry_date,
              remarks: doc.remarks,
              document_file: doc.document_file[0].originFileObj
            }));

          if (docsToUpload.length > 0) {
            await bulkUploadSubcontractorDocuments(editing.id, docsToUpload);
          }
        }
      } else {
        // Create subcontractor first
        const res = await createSubcontractor(values);
        if (!res.success) throw new Error(res.message);

        // Then upload documents if any
        if (values.documents && values.documents.length > 0) {
          const docsToUpload = values.documents
            .filter(doc => doc.document_file?.[0]?.originFileObj)
            .map(doc => ({
              document_type: doc.document_type,
              expiry_date: doc.expiry_date,
              remarks: doc.remarks,
              document_file: doc.document_file[0].originFileObj
            }));

          if (docsToUpload.length > 0) {
            await bulkUploadSubcontractorDocuments(res.data.id, docsToUpload);
          }
        }
      }

      message.success(editing ? 'Subcontractor updated' : 'Subcontractor created');
      setModalVisible(false);
      fetchSubcontractors();
    } catch (err) {
      console.error(err);
      message.error(err.message || 'Operation failed');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id) => {
    const res = await deleteSubcontractor(id);
    if (res.success) {
      message.success('Subcontractor deleted');
      fetchSubcontractors();
    } else {
      message.error(res.message || 'Delete failed');
    }
  };

  const openDocViewModal = async (record) => {
    setLoading(true);
    const res = await getSubcontractor(record.id);
    setLoading(false);
    if (res.success) {
      setViewingSub(res.data);
      setDocViewModalVisible(true);
    }
  };

  const columns = [
    {
      title: 'S.No',
      key: 'sno',
      width: 60,
      render: (_, __, index) => index + 1
    },
    { title: 'Name', dataIndex: 'name', key: 'name' },
    { title: 'Email', dataIndex: 'email', key: 'email' },
    { title: 'Phone', dataIndex: 'phone', key: 'phone' },
    { title: 'Contact Person', dataIndex: 'contact_person', key: 'contact_person' },
    { title: 'Status', dataIndex: 'status', key: 'status', width: 100 },
    {
      title: 'Actions',
      key: 'actions',
      width: 250,
      render: (_, record) => (
        <>
          <Button size="small" icon={<EditOutlined />} style={{ marginRight: 8 }} onClick={() => openModal(record)} />
          <Button size="small" icon={<FileOutlined />} style={{ marginRight: 8, color: '#1890ff' }} onClick={() => openDocViewModal(record)} />
          <Button size="small" icon={<TeamOutlined />} style={{ marginRight: 8 }} onClick={() => navigate(`/subcontractors/${record.id}/employees`)} />
          <Popconfirm title="Are you sure to delete this subcontractor?" onConfirm={() => handleDelete(record.id)} okText="Yes" cancelText="No">
            <Button size="small" icon={<DeleteOutlined />} danger />
          </Popconfirm>
        </>
      ),
    },
  ];

  // if (selectedSubId) {
  //   return <SubcontractorEmployees subcontractorId={selectedSubId} onBack={() => setSelectedSubId(null)} />;
  // }

  return (
    <div>
      <div style={{ marginBottom: 16, display: 'flex', justifyContent: 'space-between' }}>
        <h2>Subcontractors</h2>
        <Button type="primary" icon={<PlusOutlined />} onClick={() => openModal()}>Add Subcontractor</Button>
      </div>
      <Table
        columns={columns}
        dataSource={subcontractors}
        loading={loading}
        rowKey="id"
      />
      <Modal
        title={editing ? 'Edit Subcontractor' : 'Add Subcontractor'}
        open={modalVisible}
        onOk={handleOk}
        onCancel={() => setModalVisible(false)}
        okText={editing ? 'Update' : 'Create'}
        width="80vw"
        style={{ height: '70vh', maxWidth: '100vw', padding: 0 }}
        bodyStyle={{ height: 'calc(70vh - 55px)', overflow: 'auto', margin: 0, padding: 24 }}
      >
        <Form form={form} layout="vertical">
          <Row gutter={24}>
            <Col span={12}>
              <Form.Item name="name" label="Name" rules={[{ required: true, message: 'Please enter name' }]}>
                <Input />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="email" label="Email" rules={[{ type: 'email', message: 'Invalid email' }]}>
                <Input />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="phone" label="Phone">
                <Input />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="address" label="Address">
                <Input />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="contact_person" label="Contact Person">
                <Input />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="status" label="Status" initialValue="active">
                <Select>
                  <Option value="active">Active</Option>
                  <Option value="inactive">Inactive</Option>
                </Select>
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="invoice_clearance_days" label="Invoice Clearance Days">
                <Input type="number" min={0} placeholder="e.g. 30" />
              </Form.Item>
            </Col>
          </Row>

          <Divider orientation="left" style={{ borderColor: '#1890ff', marginTop: 32 }}>
            <Space><FileOutlined /> Subcontractor Documents</Space>
          </Divider>

          <div style={{ background: '#f9f9f9', padding: 20, borderRadius: 12, border: '1px solid #f0f0f0' }}>
            <Form.List name="documents">
              {(fields, { add, remove }) => (
                <>
                  {fields.map(({ key, name, ...restField }) => (
                    <Card
                      key={key}
                      size="small"
                      style={{ marginBottom: 16, border: '1px solid #e8e8e8', borderRadius: 10, boxShadow: '0 2px 4px rgba(0,0,0,0.02)' }}
                      title={<Tag color="blue">{form.getFieldValue(['documents', name, 'document_type'])?.replace(/_/g, ' ').toUpperCase() || 'NEW DOCUMENT'}</Tag>}
                      extra={<MinusCircleOutlined onClick={() => remove(name)} style={{ color: '#ff4d4f', fontSize: 18 }} />}
                    >
                      <Row gutter={16}>
                        <Col span={6}>
                          <Form.Item
                            {...restField}
                            name={[name, 'document_type']}
                            label="Document Type"
                            rules={[{ required: true, message: 'Type required' }]}
                          >
                            <Select placeholder="Select type" onChange={() => form.setFieldsValue({ documents: form.getFieldValue('documents') })}>
                              {subDocTypes.map(dt => <Option key={dt.value} value={dt.value}>{dt.label}</Option>)}
                            </Select>
                          </Form.Item>
                        </Col>
                        <Col span={6}>
                          <Form.Item
                            {...restField}
                            name={[name, 'expiry_date']}
                            label="Expiry Date"
                            getValueProps={(value) => ({
                              value: value ? new Date(value).toISOString().split('T')[0] : ''
                            })}
                          >
                            <Input type="date" />
                          </Form.Item>
                        </Col>
                        <Col span={6}>
                          <Form.Item
                            {...restField}
                            name={[name, 'document_file']}
                            label="File Upload"
                            valuePropName="fileList"
                            getValueFromEvent={e => (Array.isArray(e) ? e.slice(-1) : e && e.fileList ? e.fileList.slice(-1) : [])}
                            rules={[{ required: true, message: 'File required' }]}
                          >
                            <Upload
                              beforeUpload={() => false}
                              maxCount={1}
                              listType="picture"
                              accept=".jpg,.jpeg,.png,.pdf,.doc,.docx"
                            >
                              <Button icon={<UploadOutlined />} style={{ width: '100%' }}>Choose File</Button>
                            </Upload>
                          </Form.Item>
                        </Col>
                        <Col span={6}>
                          <Form.Item
                            {...restField}
                            name={[name, 'remarks']}
                            label="Remarks"
                          >
                            <Input placeholder="Optional remarks" />
                          </Form.Item>
                        </Col>
                      </Row>
                    </Card>
                  ))}
                  <Form.Item style={{ marginBottom: 0 }}>
                    <Button
                      type="dashed"
                      onClick={() => add()}
                      block
                      icon={<PlusOutlined />}
                      style={{ height: 45, borderRadius: 10, background: '#fff', borderStyle: 'dashed', borderWidth: 2 }}
                    >
                      Add Another Document
                    </Button>
                  </Form.Item>
                </>
              )}
            </Form.List>
          </div>
        </Form>
      </Modal>

      <Modal
        title={`Documents for ${viewingSub?.name}`}
        open={docViewModalVisible}
        onCancel={() => setDocViewModalVisible(false)}
        footer={[
          <Button key="close" onClick={() => setDocViewModalVisible(false)}>Close</Button>
        ]}
        width={900}
        bodyStyle={{ maxHeight: '70vh', overflowY: 'auto' }}
      >
        <Row gutter={[16, 16]}>
          {subDocTypes.map(section => {
            const docs = viewingSub?.documents?.filter(d => d.document_type === section.value) || [];
            return (
              <Col span={12} key={section.value}>
                <Card
                  title={<Space><Tag color="blue">{docs.length}</Tag> {section.label}</Space>}
                  size="small"
                  style={{ height: '100%', borderRadius: 12, border: '1px solid #f0f0f0', boxShadow: '0 2px 8px rgba(0,0,0,0.05)' }}
                >
                  {docs.length > 0 ? (
                    <List
                      size="small"
                      dataSource={docs}
                      renderItem={doc => (
                        <List.Item
                          extra={
                            <a href={`${imagepath}${doc.document_file}`} target="_blank" rel="noopener noreferrer">
                              <Button type="link" icon={isImage(doc.document_file) ? <FileOutlined /> : <FileOutlined />}>View</Button>
                            </a>
                          }
                        >
                          <List.Item.Meta
                            title={<Text strong style={{ fontSize: 13 }}>{doc.original_filename}</Text>}
                            description={
                              <Space direction="vertical" size={0}>
                                {doc.expiry_date && <Text type="secondary" style={{ fontSize: 12 }}>Expiry: {doc.expiry_date}</Text>}
                                {doc.remarks && <Text type="secondary" style={{ fontSize: 12 }}>{doc.remarks}</Text>}
                              </Space>
                            }
                          />
                        </List.Item>
                      )}
                    />
                  ) : (
                    <div style={{ textAlign: 'center', color: '#ccc', padding: '20px 0' }}>No {section.label} uploaded</div>
                  )}
                </Card>
              </Col>
            );
          })}
        </Row>
      </Modal>
    </div>
  );
};

export default Subcontractors; 